import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { X, Send, Sparkles, User, Loader2, BookOpen, HelpCircle } from "lucide-react";
import { useAuth, API } from "@/App";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const AIChatbot = ({ onClose, userRole = "client" }) => {
  const { token } = useAuth();
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: `Hi! I'm your MyHairChair AI Assistant. I can help you with:

• **App Features** - How to use booking, payments, rewards
• **Hair & Beauty Tips** - Styling advice and recommendations
• **Troubleshooting** - Fix common issues
• **Finding Services** - What's available and how it works

What can I help you with today?`
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/ai/chat`,
        { message: userMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
    } catch (error) {
      toast.error("Failed to get response");
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I'm sorry, I couldn't process your request. Please try again or check the User Guide for help." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestion = (suggestion) => {
    setInput(suggestion);
    inputRef.current?.focus();
  };

  // Role-specific suggestions
  const getSuggestions = () => {
    switch (userRole) {
      case "stylist":
        return [
          "How do I set up payment methods?",
          "How do I verify a receipt?",
          "How do I add services?"
        ];
      case "owner":
        return [
          "How do I add booth renters?",
          "How do I track revenue?",
          "How do rent payments work?"
        ];
      default:
        return [
          "How do I book an appointment?",
          "How do I upload a payment receipt?",
          "How do loyalty points work?"
        ];
    }
  };

  return (
    <div 
      className="fixed bottom-24 right-6 w-96 max-w-[calc(100vw-48px)] bg-white rounded-2xl shadow-2xl border border-stone-200 overflow-hidden z-50 animate-fade-in"
      data-testid="ai-chatbot"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-violet-500 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-display font-semibold text-white">AI Assistant</h3>
              <p className="text-xs text-white/80">Ask me anything about MyHairChair</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link 
              to="/user-guide"
              className="text-white/80 hover:text-white transition-colors p-1"
              title="View User Guide"
              data-testid="open-guide-from-chat"
            >
              <BookOpen className="w-5 h-5" />
            </Link>
            <button 
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors p-1"
              data-testid="close-chatbot-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="h-80 p-4" ref={scrollRef}>
        <div className="space-y-4">
          {messages.map((msg, index) => (
            <div 
              key={index}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div className={`flex items-start gap-2 max-w-[85%] ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.role === "user" ? "bg-emerald-100" : "bg-violet-100"
                }`}>
                  {msg.role === "user" ? (
                    <User className="w-4 h-4 text-emerald-700" />
                  ) : (
                    <Sparkles className="w-4 h-4 text-violet-600" />
                  )}
                </div>
                <div className={`chat-bubble ${
                  msg.role === "user" ? "chat-bubble-user" : "chat-bubble-ai"
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="flex justify-start">
              <div className="flex items-start gap-2">
                <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-violet-600" />
                </div>
                <div className="chat-bubble chat-bubble-ai">
                  <Loader2 className="w-4 h-4 animate-spin text-violet-600" />
                </div>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Suggestions */}
      {messages.length <= 2 && (
        <div className="px-4 pb-2 flex flex-wrap gap-2">
          {getSuggestions().map((suggestion, index) => (
            <button
              key={index}
              onClick={() => handleSuggestion(suggestion)}
              className="text-xs bg-violet-50 text-violet-700 px-3 py-1.5 rounded-full hover:bg-violet-100 transition-colors"
              data-testid={`suggestion-${index}`}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}

      {/* Quick Links */}
      <div className="px-4 pb-2 flex items-center justify-center gap-2 border-t border-stone-100 pt-2">
        <Link 
          to="/user-guide"
          className="text-xs text-violet-600 hover:text-violet-800 flex items-center gap-1"
        >
          <BookOpen className="w-3 h-3" />
          User Guide
        </Link>
        <span className="text-stone-300">•</span>
        <Link 
          to="/faq"
          className="text-xs text-violet-600 hover:text-violet-800 flex items-center gap-1"
        >
          <HelpCircle className="w-3 h-3" />
          FAQ
        </Link>
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-4 border-t border-stone-200">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about features, booking, payments..."
            className="flex-1 bg-stone-50"
            disabled={loading}
            data-testid="chat-input"
          />
          <Button 
            type="submit" 
            size="icon"
            disabled={!input.trim() || loading}
            className="bg-violet-600 hover:bg-violet-700"
            data-testid="send-message-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AIChatbot;
